import React, { useCallback, useContext } from 'react'
import { TickerThreadContext } from '@components/MessageThread/TickerThreadContext'
import { Button, Stack } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useShortcut } from 'hooks/useShortcut'
import { MotionIconButton } from 'ui/components/Motion/MotionComponents'

import { dispatchMockEnterEvent } from '../utils/helpers'

export const EditMessageButtons = (props: {
    onSave?: () => void
    onCancel?: () => void
    isEditing: boolean
    isEditorEmpty: boolean
    hasImage: boolean
    verticalButtons?: boolean
    disabled?: boolean
    renderSendButton?: (onSend: () => void) => React.ReactNode
}) => {
    const { isTouch } = useDevice()
    const {
        onCancel,
        onSave,
        isEditing,
        isEditorEmpty,
        hasImage,
        verticalButtons,
        renderSendButton,
    } = props

    const handleDismiss = useCallback(() => {
        onCancel?.()
    }, [onCancel])

    useShortcut('DismissDialog', handleDismiss, {
        enabled: !!onCancel,
    })

    const cancelButtonPressed = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            onCancel?.()
        },
        [onCancel],
    )

    /**
     * Dispatches a mock Enter event to the active element (editor).
     * This is used in ExitComboboxPlugin.ts plugin to convert any unselected combobox item to a paragraph node.
     * E.g. :) or @ me
     *
     * Only required for touch devices, because on touch, we don't use Enter key to send the message.
     */
    const saveButtonPressed = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            if (isTouch) {
                dispatchMockEnterEvent()
                onSave?.()
            }
            // For desktop, we don't need to invoke onSave(), if editor is focused,
            // because we listen to 'Enter' key event in PlateEditor.tsx
            // Calling onSave() here will send the message twice
            else {
                onSave?.()
            }
        },
        [onSave, isTouch],
    )

    const disabled = props.disabled || (isEditorEmpty && !hasImage)

    /**
     * If we are on touch device, we need to use `onMouseDown` to prevent the
     * default behavior of the button. Otherwise, the button will get focus and the
     * software keyboard will disappear
     *
     * If we are on desktop, we use `onClick` event, because `onMouseDown` event
     * will not trigger `onBlur` events attached to editor, e.g. closing the combobox gracefully
     * @see: `createComboboxPlugin` in ../plugins/index.ts
     */
    const clickHandlers = {
        cancel: {
            [isTouch ? 'onMouseDown' : 'onClick']: cancelButtonPressed,
        },
        save: {
            [isTouch ? 'onMouseDown' : 'onClick']: saveButtonPressed,
        },
    }

    const isTradingContext = !!useContext(TickerThreadContext)

    return (
        <Stack
            horizontal
            gap="sm"
            data-testid="editor-cta-buttons"
            justifyContent={verticalButtons ? 'start' : 'end'}
            paddingBottom="xs"
            paddingX="xs"
        >
            {isEditing ? (
                <>
                    <Button size="button_xs" data-testid="cancel" {...clickHandlers.cancel}>
                        Cancel
                    </Button>
                    <Button
                        size="button_xs"
                        data-testid="submit"
                        tone="cta1"
                        disabled={disabled}
                        {...clickHandlers.save}
                    >
                        Save
                    </Button>
                </>
            ) : isTouch ? (
                (onSave && renderSendButton?.(onSave)) || (
                    <MotionIconButton
                        whileHover={
                            disabled
                                ? {}
                                : {
                                      opacity: 0.8,
                                  }
                        }
                        data-testid="submit"
                        icon={disabled ? 'touchSendDisabled' : 'touchSendEnabled'}
                        size="square_lg"
                        padding="none"
                        onMouseDown={disabled ? cancelButtonPressed : saveButtonPressed}
                    />
                )
            ) : (
                <>
                    {(onSave && renderSendButton?.(onSave)) || (
                        <MotionIconButton
                            whileHover={
                                disabled
                                    ? {}
                                    : {
                                          opacity: 0.8,
                                      }
                            }
                            data-testid="submit"
                            icon={disabled ? 'touchSendDisabled' : 'touchSendEnabled'}
                            size={isTradingContext ? 'square_lg' : 'square_md'}
                            padding="none"
                            onClick={disabled ? undefined : saveButtonPressed}
                        />
                    )}
                </>
            )}
        </Stack>
    )
}
